import React, { useState } from 'react';
import api from '../../services/api';

interface FeedbackModalProps {
  isOpen: boolean;
  offerId: string;
  offerNumber?: string;
  link?: string;
  contact?: string;
  channel?: 'sms' | 'email';
  authorizedPersonName?: string;
  onSubmit: () => void;
}

function truncateLink(url: string): string {
  try {
    const u = new URL(url);
    const token = u.searchParams.get('token') ?? '';
    return `${u.host}${u.pathname}?token=${token.slice(0, 10)}…`;
  } catch {
    return url.slice(0, 40) + '…';
  }
}

// SVG outline smiley faces — black stroke, no fill
const FACES: React.FC<{ selected: boolean }>[] = [
  // 1 — Very Unsatisfied
  ({ selected }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke={selected ? '#00A84F' : '#1a1a2e'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="8.5" cy="9.5" r="1.2" fill={selected ? '#00A84F' : '#1a1a2e'} stroke="none" />
      <circle cx="15.5" cy="9.5" r="1.2" fill={selected ? '#00A84F' : '#1a1a2e'} stroke="none" />
      {/* eyebrows tilted inward */}
      <path d="M 6.5 7.5 L 9.5 8.5" />
      <path d="M 17.5 7.5 L 14.5 8.5" />
      {/* deep frown */}
      <path d="M 7.5 17 Q 12 13 16.5 17" />
    </svg>
  ),
  // 2 — Unsatisfied
  ({ selected }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke={selected ? '#00A84F' : '#1a1a2e'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="8.5" cy="9.5" r="1.2" fill={selected ? '#00A84F' : '#1a1a2e'} stroke="none" />
      <circle cx="15.5" cy="9.5" r="1.2" fill={selected ? '#00A84F' : '#1a1a2e'} stroke="none" />
      {/* slight frown */}
      <path d="M 8.5 16.5 Q 12 14 15.5 16.5" />
    </svg>
  ),
  // 3 — Neutral
  ({ selected }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke={selected ? '#00A84F' : '#1a1a2e'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="8.5" cy="9.5" r="1.2" fill={selected ? '#00A84F' : '#1a1a2e'} stroke="none" />
      <circle cx="15.5" cy="9.5" r="1.2" fill={selected ? '#00A84F' : '#1a1a2e'} stroke="none" />
      {/* straight mouth */}
      <line x1="8.5" y1="15.5" x2="15.5" y2="15.5" />
    </svg>
  ),
  // 4 — Satisfied
  ({ selected }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke={selected ? '#00A84F' : '#1a1a2e'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="8.5" cy="9.5" r="1.2" fill={selected ? '#00A84F' : '#1a1a2e'} stroke="none" />
      <circle cx="15.5" cy="9.5" r="1.2" fill={selected ? '#00A84F' : '#1a1a2e'} stroke="none" />
      {/* slight smile */}
      <path d="M 8.5 15 Q 12 17.5 15.5 15" />
    </svg>
  ),
  // 5 — Very Satisfied
  ({ selected }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke={selected ? '#00A84F' : '#1a1a2e'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="8.5" cy="9.5" r="1.2" fill={selected ? '#00A84F' : '#1a1a2e'} stroke="none" />
      <circle cx="15.5" cy="9.5" r="1.2" fill={selected ? '#00A84F' : '#1a1a2e'} stroke="none" />
      {/* big smile */}
      <path d="M 7 14.5 Q 12 19.5 17 14.5" />
    </svg>
  ),
];

const LABELS = ['Very Unsatisfied', 'Unsatisfied', 'Neutral', 'Satisfied', 'Very Satisfied'];

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  offerId,
  offerNumber,
  link,
  contact,
  channel = 'sms',
  authorizedPersonName,
  onSubmit,
}) => {
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = () => {
    if (!link) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      // Fallback for HTTP / non-secure contexts where clipboard API is unavailable
      const el = document.createElement('textarea');
      el.value = link;
      el.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDone = async () => {
    setSubmitting(true);
    try {
      if (rating !== null) {
        await api.post(`/offers/${offerId}/feedback`, {
          rating,
          comment: comment.trim() || undefined,
        });
      }
    } catch {
      // Non-critical
    } finally {
      setSubmitting(false);
      onSubmit();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '600px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          position: 'relative',
          paddingTop: '52px',
        }}
      >
        {/* Floating checkmark */}
        <div
          style={{
            position: 'absolute',
            top: '-36px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: '#00A84F',
            border: '5px solid #fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(0,168,79,0.35)',
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Header */}
        <div style={{ padding: '0 32px 20px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '22px', fontWeight: 800, margin: '0 0 4px', color: '#0f172a' }}>
            Success!
          </h2>
          <p style={{ fontSize: '14px', fontWeight: 700, margin: 0, color: '#0f172a' }}>
            Your Offer Has Been Sent to the Customer
          </p>
        </div>

        {/* Success message box */}
        <div style={{ padding: '0 32px 20px' }}>
          <div
            style={{
              border: '1.5px solid #00A84F',
              borderRadius: '10px',
              padding: '14px 16px',
              background: '#f0fdf4',
            }}
          >
            <p style={{ fontSize: '13px', color: '#374151', margin: '0 0 12px', lineHeight: '1.6' }}>
              Your offer{offerNumber ? <> <strong style={{ color: '#00A84F' }}>{offerNumber}</strong></> : ''} has been sent
              {authorizedPersonName ? <> to <strong>{authorizedPersonName}</strong></> : ''}{' '}
              with a link to accept or reject the offer
              {contact ? <> on their registered {channel === 'sms' ? 'mobile' : 'email'} (<strong>{contact}</strong>)</> : ''}.{' '}
              The link expires in <strong>7 days</strong>.
            </p>

            {link && (
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <span
                  style={{
                    flex: 1,
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    color: '#1a1a2e',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={link}
                >
                  {truncateLink(link)}
                </span>
                <button
                  onClick={handleCopy}
                  style={{
                    flexShrink: 0,
                    padding: '5px 14px',
                    background: copied ? '#00A84F' : '#0f172a',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'background 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {copied ? '✓ Copied' : 'Copy Link'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: '#e9ecef', margin: '0 32px' }} />

        {/* Feedback section */}
        <div
          style={{
            padding: '20px 32px 24px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            alignItems: 'start',
          }}
        >
          {/* Left: rating */}
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
              Please Rate Your Experience
            </div>
            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '14px' }}>
              Your feedback helps us improve our services for you each time.
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {FACES.map((Face, i) => {
                const val = i + 1;
                const selected = rating === val;
                return (
                  <button
                    key={val}
                    title={LABELS[i]}
                    onClick={() => setRating(val)}
                    style={{
                      background: selected ? '#e6f7ed' : 'transparent',
                      border: `2px solid ${selected ? '#00A84F' : '#dee2e6'}`,
                      borderRadius: '50%',
                      width: '44px',
                      height: '44px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      transition: 'all 0.12s',
                      transform: selected ? 'scale(1.15)' : 'scale(1)',
                    }}
                  >
                    <Face selected={selected} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: comment */}
          <div>
            <textarea
              placeholder="Write your feedback here !"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1.5px solid #dee2e6',
                borderRadius: '8px',
                fontSize: '13px',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                color: '#374151',
                background: '#f8f9fa',
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '0 32px 28px' }}>
          <button
            onClick={handleDone}
            disabled={submitting}
            style={{
              padding: '11px 36px',
              background: '#0f172a',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Saving…' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
};
